/**
 * ewayBill.ts — Build the e-Way Bill "bulk generation" JSON accepted by the
 * EWB portal's offline tool, from BigxBook sale invoices + transport details.
 *
 * An e-way bill is mandatory for movement of goods where the consignment value
 * exceeds ₹50,000. This module produces the JSON array the bulk tool imports,
 * validates each consignment, and exposes the threshold check.
 *
 * Pure — no DB, no I/O. The data source / UI collect transport details and
 * party info; the caller writes the JSON via `file.saveText`.
 */

import { round2 } from '../gst/gstReturns';
import { toUqc } from '../gst/gstReturns';
import { stateCodeFromGstin, stateCodeFromName } from '../gst/gstStates';

/** Consignment value above which an e-way bill is mandatory. */
export const EWB_THRESHOLD = 50000;

export type TransMode = '1' | '2' | '3' | '4'; // Road, Rail, Air, Ship
export type VehicleType = 'R' | 'O'; // Regular, Over-Dimensional Cargo

export interface EwbParty {
  gstin: string | null; // null / '' → treated as URP (unregistered)
  tradeName: string;
  address1: string;
  place: string; // city/town
  pincode: number | null;
  /** Free-text state; converted to the numeric GST state code. */
  state: string | null;
}

export interface EwbItemInput {
  productName: string;
  hsnCode: string | null;
  quantity: number;
  unit: string | null;
  taxableAmount: number;
  cgstRate: number;
  sgstRate: number;
  igstRate: number;
  cessRate: number;
}

export interface EwbTransport {
  mode: TransMode;
  distanceKm: number;
  vehicleNo?: string;
  vehicleType?: VehicleType;
  transporterId?: string;
  transporterName?: string;
  transDocNo?: string;
  transDocDate?: Date;
}

export interface EwbInvoiceInput {
  docNo: string;
  docDate: Date;
  supplyType: 'O' | 'I'; // Outward / Inward
  from: EwbParty;
  to: EwbParty;
  totalTaxable: number;
  cgst: number;
  sgst: number;
  igst: number;
  cess: number;
  totInvValue: number;
  items: EwbItemInput[];
  transport: EwbTransport;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtDate(d: Date): string {
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}

function stateNum(party: EwbParty): number {
  const code = stateCodeFromGstin(party.gstin) ?? stateCodeFromName(party.state);
  return code ? parseInt(code, 10) : 0;
}

function hsnNum(hsn: string | null): number {
  const n = parseInt((hsn ?? '').replace(/\D/g, ''), 10);
  return Number.isFinite(n) ? n : 0;
}

/** True when a consignment legally requires an e-way bill. */
export function requiresEwayBill(invoiceValue: number): boolean {
  return invoiceValue > EWB_THRESHOLD;
}

/**
 * Validate a consignment; returns human-readable errors (empty = valid).
 * Vehicle number is required for road transport unless a transporter doc is
 * supplied; a transporter ID is required otherwise.
 */
export function validateEwb(inv: EwbInvoiceInput): string[] {
  const errs: string[] = [];
  if (!inv.docNo) errs.push('Missing invoice number.');
  if (stateNum(inv.from) === 0) errs.push('Cannot resolve the "from" state — set the store GSTIN/state.');
  if (stateNum(inv.to) === 0) errs.push('Cannot resolve the "to" state — set the customer state/GSTIN.');
  if (!inv.from.pincode) errs.push('Missing "from" pincode.');
  if (!inv.to.pincode) errs.push('Missing "to" pincode.');
  if (!(inv.transport.distanceKm > 0)) errs.push('Enter the transport distance (km).');
  const t = inv.transport;
  const hasVehicle = !!t.vehicleNo?.trim();
  const hasTransporter = !!t.transporterId?.trim() || (!!t.transDocNo?.trim() && !!t.transDocDate);
  if (t.mode === '1' && !hasVehicle && !hasTransporter) {
    errs.push('Road transport needs a vehicle number or a transporter/LR document.');
  }
  if (t.mode !== '1' && !hasTransporter) {
    errs.push('Rail/Air/Ship transport needs a transporter document number and date.');
  }
  if (inv.items.length === 0) errs.push('No line items.');
  return errs;
}

// ─── Builder ─────────────────────────────────────────────────────────────────

function buildOne(inv: EwbInvoiceInput): Record<string, unknown> {
  const fromState = stateNum(inv.from);
  const toState = stateNum(inv.to);
  const t = inv.transport;
  return {
    userGstin: inv.from.gstin ?? '',
    supplyType: inv.supplyType,
    subSupplyType: '1', // Supply
    docType: 'INV',
    docNo: inv.docNo,
    docDate: fmtDate(inv.docDate),
    fromGstin: inv.from.gstin ?? 'URP',
    fromTrdName: inv.from.tradeName,
    fromAddr1: inv.from.address1,
    fromPlace: inv.from.place,
    fromPincode: inv.from.pincode ?? 0,
    fromStateCode: fromState,
    actFromStateCode: fromState,
    toGstin: inv.to.gstin ?? 'URP',
    toTrdName: inv.to.tradeName,
    toAddr1: inv.to.address1,
    toPlace: inv.to.place,
    toPincode: inv.to.pincode ?? 0,
    toStateCode: toState,
    actToStateCode: toState,
    transactionType: 1, // Regular
    totalValue: round2(inv.totalTaxable),
    cgstValue: round2(inv.cgst),
    sgstValue: round2(inv.sgst),
    igstValue: round2(inv.igst),
    cessValue: round2(inv.cess),
    totInvValue: round2(inv.totInvValue),
    transporterId: t.transporterId ?? '',
    transporterName: t.transporterName ?? '',
    transDocNo: t.transDocNo ?? '',
    transDocDate: t.transDocDate ? fmtDate(t.transDocDate) : '',
    transMode: t.mode,
    transDistance: String(Math.round(t.distanceKm)),
    vehicleNo: (t.vehicleNo ?? '').toUpperCase().replace(/\s/g, ''),
    vehicleType: t.vehicleType ?? 'R',
    itemList: inv.items.map((it, idx) => ({
      itemNo: idx + 1,
      productName: it.productName,
      hsnCode: hsnNum(it.hsnCode),
      quantity: round2(it.quantity),
      qtyUnit: toUqc(it.unit),
      taxableAmount: round2(it.taxableAmount),
      cgstRate: it.cgstRate,
      sgstRate: it.sgstRate,
      igstRate: it.igstRate,
      cessRate: it.cessRate,
    })),
  };
}

/** Build the EWB bulk-generation JSON array from consignments. */
export function buildEwayBillJson(invoices: EwbInvoiceInput[]): Record<string, unknown>[] {
  return invoices.map(buildOne);
}

/** Convenience: pretty JSON string ready for `file.saveText`. */
export function stringifyEwayBill(invoices: EwbInvoiceInput[]): string {
  return JSON.stringify(buildEwayBillJson(invoices), null, 2);
}
